"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { authService } from "@/lib/auth";
import type { User } from "@/types/auth";
import { UserCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  UserCircleIcon as UserCircleIconSolid,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";

export default function UserMenu() {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = await authService.me();
          setUser(userData);
        }
      } catch (error) {
        console.error("Erro ao carregar usuário:", error);
      }
    };

    loadUser();
  }, []);

  useEffect(() => {
    // Fechar menu ao clicar fora
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      router.push("/login");
      router.refresh();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  if (!user) {
    return null;
  }

  // Pegar primeiro nome
  const firstName = user.name.split(" ")[0];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-white transition-all hover:bg-white/20"
      >
        <UserCircleIcon className="h-6 w-6" />
        <span className="hidden md:inline font-medium">{firstName}</span>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-lg ring-1 ring-black/5">
          {/* User Info */}
          <div className="border-b border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-mygreen/10">
                <UserCircleIconSolid className="h-8 w-8 text-mygreen" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {user.name}
                </p>
                <p className="text-sm text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-2">
            {user.role === "company" && user.company && (
              <Link
                href="/minha-empresa"
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
                Minha Empresa
              </Link>
            )}

            <Link
              href="/perfil"
              className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
              Configurações
            </Link>

            <Link
              href="/exclusao-de-conta"
              className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <TrashIcon className="h-5 w-5 text-gray-400" />
              Excluir Conta
            </Link>

            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
